import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, formatTarih } from '../../lib/api';
import { markdownHtml, panelGorselleri } from './markdown';
import { AramaOnizleme } from './SiteOzellestir';
import { sor } from '../../ui/Sor';
import {
  Alan, GorselAlan, GorselSecici, Kart, KaydetCubugu, SayfaBaslik, Secmeli, SiteGorsel, Sonuc, YayinDugmesi, gorselAdresi,
  useCikisUyarisi, useSiteDurum, useTaslak, type Medya,
} from './ortak';

type YaziOzet = {
  id: number; slug: string; lang: 'tr' | 'en'; title: string; excerpt: string; cover: string; status: 'TASLAK' | 'YAYINDA';
  publishedAt: string | null; seoTitle: string | null; seoDesc: string | null; updatedAt: string;
};
type Yazi = YaziOzet & { bodyMd: string };
type Form = Omit<Yazi, 'id' | 'updatedAt'>;

const duz = (s: string) => s.toLocaleLowerCase('tr');
const DURUM_SEKME = [['hepsi', 'Tümü'], ['YAYINDA', 'Yayında'], ['TASLAK', 'Taslak']] as const;

/** Site › Blog yazıları — dizin: durum sekmeleri, arama, dil süzgeci. */
export function SiteBlog() {
  const navigate = useNavigate();
  const { durum, yenile } = useSiteDurum();
  const [liste, setListe] = useState<YaziOzet[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [sekme, setSekme] = useState<(typeof DURUM_SEKME)[number][0]>('hepsi');
  const [dil, setDil] = useState<'hepsi' | 'tr' | 'en'>('hepsi');
  const [ara, setAra] = useState('');
  useEffect(() => {
    api<{ yazilar: YaziOzet[] }>('/api/site/blog').then((r) => setListe(r.yazilar)).catch((e) => setHata((e as Error).message));
  }, []);
  const q = duz(ara.trim());
  const dilde = (liste ?? []).filter((y) => (dil === 'hepsi' || y.lang === dil) && (!q || duz(`${y.title} ${y.slug}`).includes(q)));
  const gorunen = dilde.filter((y) => sekme === 'hepsi' || y.status === sekme);

  return (
    <div className="ccd-stack">
      <SayfaBaslik baslik="Blog yazıları" durum={durum}>
        <YayinDugmesi durum={durum} yenile={() => void yenile()} birincil={false} />
        <Link className="ccd-btn ccd-btn-primary" to="/site/blog/yeni">Yazı ekle</Link>
      </SayfaBaslik>
      <Sonuc bilgi={null} hata={hata} />

      <div className="ccd-card ccd-card-flush ccd-site-dizin-kart">
        <div className="ccd-site-arac">
          <div className="ccd-tabs" role="tablist">
            {DURUM_SEKME.map(([id, ad]) => (
              <button key={id} role="tab" aria-selected={sekme === id} className={`ccd-tab${sekme === id ? ' ccd-is-active' : ''}`} onClick={() => setSekme(id)}>
                {ad}{liste ? <span className="ccd-site-adet">{dilde.filter((y) => id === 'hepsi' || y.status === id).length}</span> : null}
              </button>
            ))}
          </div>
          <div className="ccd-site-suz">
            <input className="ccd-input" type="search" placeholder="Yazılarda ara" aria-label="Yazılarda ara" value={ara} onChange={(e) => setAra(e.target.value)} />
            <Secmeli etiket="Dil süzgeci" secenekler={[['hepsi', 'Tüm diller'], ['tr', 'TR'], ['en', 'EN']] as const} deger={dil} degis={setDil} />
          </div>
        </div>
        {!liste ? (!hata && <div className="ccd-site-bosluk ccd-inline-note">Yükleniyor…</div>) : gorunen.length === 0 ? (
          <div className="ccd-site-bosluk">
            <div className="ccd-site-kart-baslik">{liste.length === 0 ? 'Henüz yazı yok' : 'Bu süzgeçte yazı yok'}</div>
            {liste.length === 0 && <Link className="ccd-btn ccd-btn-sm" to="/site/blog/yeni">Yazı ekle</Link>}
          </div>
        ) : (
          <div className="ccd-site-dizin-sar">
            <table className="ccd-table ccd-site-dizin">
              <thead>
                <tr><th className="ccd-th" aria-label="Kapak" /><th className="ccd-th">Başlık</th><th className="ccd-th">Durum</th><th className="ccd-th ccd-site-dar-gizle">Dil</th><th className="ccd-th ccd-site-dar-gizle">Tarih</th></tr>
              </thead>
              <tbody>
                {gorunen.map((y) => (
                  <tr className="ccd-tr ccd-tr-clickable" key={y.id} onClick={() => navigate(`/site/blog/${y.id}`)}>
                    <td className="ccd-td ccd-site-dizin-resim"><SiteGorsel yol={y.cover} className="ccd-site-kucukresim" /></td>
                    <td className="ccd-td ccd-site-dizin-ad">
                      <Link className="ccd-site-dizin-bag" to={`/site/blog/${y.id}`} onClick={(e) => e.stopPropagation()}>{y.title}</Link>
                      <div className="ccd-meta ccd-mono ccd-truncate">/{y.lang === 'en' ? 'en/' : ''}blog/{y.slug}</div>
                    </td>
                    <td className="ccd-td"><span className={`ccd-badge${y.status === 'YAYINDA' ? ' ccd-badge-success' : ''}`}>{y.status === 'YAYINDA' ? 'Yayında' : 'Taslak'}</span></td>
                    <td className="ccd-td ccd-site-dar-gizle">{y.lang.toUpperCase()}</td>
                    <td className="ccd-td ccd-td-nowrap ccd-site-dar-gizle">{y.publishedAt ? formatTarih(`${y.publishedAt}T00:00:00Z`) : <span className="ccd-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const BOS: Form = {
  slug: '', lang: 'tr', title: '', excerpt: '', cover: '', status: 'TASLAK', publishedAt: null, seoTitle: null, seoDesc: null, bodyMd: '',
};

/** Başlıktan adres önerisi — sunucudaki `slugUret` ile aynı kural. */
function slugOner(ham: string): string {
  const harf: Record<string, string> = { ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', â: 'a', î: 'i', û: 'u' };
  return ham.toLocaleLowerCase('tr').replace(/[çğıöşüâîû]/g, (h) => harf[h] ?? h).normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/g, '');
}

/** Site › Blog yazıları › yazı — solda içerik, sağda görünürlük, kapak, dil, adres ve arama motoru kartları. */
export function SiteYazi() {
  const { id } = useParams();
  const yeni = id === 'yeni';
  const navigate = useNavigate();
  const { durum, yenile } = useSiteDurum();
  const { f, setF, yukle, kirli, vazgec } = useTaslak<Form>();
  /** Adres elle değiştirilmediyse başlığı izler (yalnız yeni yazıda). */
  const [slugElle, setSlugElle] = useState(!yeni);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [gorselSec, setGorselSec] = useState(false);
  const [icerikSekme, setIcerikSekme] = useState<'yaz' | 'onizle'>('yaz');
  const [seoAcik, setSeoAcik] = useState(false);
  const [adresler, setAdresler] = useState<Record<string, string>>({});
  const govde = useRef<HTMLTextAreaElement>(null);
  useCikisUyarisi(kirli);

  useEffect(() => {
    setBilgi(null); setHata(null); setIcerikSekme('yaz');
    if (yeni) { yukle(BOS); setSlugElle(false); return; }
    setSlugElle(true);
    api<Yazi>(`/api/site/blog/${id}`).then(({ id: _id, updatedAt: _u, ...y }) => yukle(y)).catch((e) => setHata((e as Error).message));
  }, [id, yeni, yukle]);

  // Önizlemedeki kütüphane görselleri jetonla çekilir (yazı değiştikçe eksikler tamamlanır).
  const govdeMetni = f?.bodyMd ?? '';
  useEffect(() => {
    let iptal = false;
    for (const yol of panelGorselleri(govdeMetni)) {
      if (adresler[yol]) continue;
      void gorselAdresi(yol).then((a) => { if (!iptal && a) setAdresler((o) => (o[yol] ? o : { ...o, [yol]: a })); }).catch(() => undefined);
    }
    return () => { iptal = true; };
  }, [govdeMetni]); // eslint-disable-line react-hooks/exhaustive-deps

  const onizleme = useMemo(() => (icerikSekme === 'onizle' ? markdownHtml(govdeMetni, (yol) => adresler[yol]
    ?? (yol.startsWith('/img/') && !yol.startsWith('/img/panel/') && durum?.siteAdresi ? `${durum.siteAdresi.replace(/\/$/, '')}${yol}` : yol)) : ''),
  [icerikSekme, govdeMetni, adresler, durum?.siteAdresi]);

  if (!f) return <div className="ccd-stack"><SayfaBaslik baslik="Yazı" geri={{ to: '/site/blog', ad: 'Blog yazıları' }} /><Sonuc bilgi={null} hata={hata} />{!hata && <div className="ccd-inline-note">Yükleniyor…</div>}</div>;
  const al = <K extends keyof Form>(k: K, v: Form[K]) => { setBilgi(null); setF((o) => (o ? { ...o, [k]: v } : o)); };

  /** Seçili metni sarar ya da satır başına önek koyar; imleç eklenen yerin sonunda kalır. */
  function ekle(once: string, sonra = '', yerTutucu = '') {
    const t = govde.current;
    if (!t) return;
    const bas = t.selectionStart, son = t.selectionEnd;
    const secili = t.value.slice(bas, son) || yerTutucu;
    al('bodyMd', `${t.value.slice(0, bas)}${once}${secili}${sonra}${t.value.slice(son)}`);
    window.requestAnimationFrame(() => { t.focus(); t.setSelectionRange(bas + once.length, bas + once.length + secili.length); });
  }
  function satirOneki(onek: string) {
    const t = govde.current;
    if (!t) return;
    const bas = t.value.lastIndexOf('\n', t.selectionStart - 1) + 1;
    const son = t.selectionEnd;
    const blok = t.value.slice(bas, son) || 'metin';
    const yeniBlok = blok.split('\n').map((s, i) => `${onek === '1. ' ? `${i + 1}. ` : onek}${s}`).join('\n');
    al('bodyMd', `${t.value.slice(0, bas)}${yeniBlok}${t.value.slice(son)}`);
    window.requestAnimationFrame(() => { t.focus(); t.setSelectionRange(bas, bas + yeniBlok.length); });
  }
  async function baglantiEkle() {
    const u = await sor({
      baslik: 'Bağlantı ekle', etiket: 'Bağlantı adresi', yerTutucu: 'https://… ya da /urunler', zorunlu: true, azami: 1000, onay: 'Bağlantıyı ekle',
      not: 'Dış adres https:// ile, site içi sayfa / ile başlar.',
      denetle: (x) => (/^(https?:\/\/\S+\.\S+|\/\S*)$/i.test(x) ? null : 'Adres https:// ile ya da site içi sayfa için / ile başlamalı.'),
    });
    if (u) ekle('[', `](${u})`, 'bağlantı yazısı');
  }
  function gorselEkle(yol: string, m: Medya) {
    setGorselSec(false);
    ekle(`\n\n![${(m.alt || '').replace(/[[\]]/g, '')}](${yol})\n\n`);
  }

  async function kaydet() {
    setBilgi(null); setHata(null); setMesgul(true);
    try {
      const govdeJson = JSON.stringify({ ...f, slug: slugElle ? f!.slug : '', publishedAt: f!.publishedAt || null });
      const r = yeni
        ? await api<{ yazi: Yazi }>('/api/site/blog', { method: 'POST', body: govdeJson })
        : await api<{ yazi: Yazi }>(`/api/site/blog/${id}`, { method: 'PUT', body: govdeJson });
      const { id: yeniId, updatedAt: _u, ...y } = r.yazi;
      yukle(y); setSlugElle(true);
      setBilgi(y.status === 'YAYINDA' ? 'Kaydedildi. Sitede görünmesi için yayınlayın.' : 'Taslak kaydedildi.');
      await yenile();
      if (yeni) navigate(`/site/blog/${yeniId}`, { replace: true });
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
    finally { setMesgul(false); }
  }

  async function sil() {
    if (!window.confirm(`“${f!.title}” silinsin mi? ${f!.status === 'YAYINDA' ? 'Yazı bir sonraki yayında siteden de kalkar. ' : ''}Bu işlem geri alınamaz.`)) return;
    setHata(null);
    try { await api(`/api/site/blog/${id}`, { method: 'DELETE' }); yukle(f!); navigate('/site/blog'); }
    catch (err) { setHata(err instanceof Error ? err.message : 'Silinemedi'); }
  }

  const adres = slugElle ? f.slug : slugOner(f.title);
  const tamAdres = `${(durum?.siteAdresi ?? '').replace(/^https?:\/\//, '').replace(/\/$/, '')}/${f.lang === 'en' ? 'en/' : ''}blog/${adres || '…'}`;
  const ARAC: [string, string, () => void, React.ReactNode?][] = [
    ['Kalın', 'K', () => ekle('**', '**', 'kalın'), <b key="k">K</b>], ['İtalik', 'İ', () => ekle('*', '*', 'italik'), <i key="i">İ</i>],
    ['Başlık', 'Başlık', () => satirOneki('## ')], ['Alt başlık', 'Alt başlık', () => satirOneki('### ')],
    ['Madde listesi', '• Liste', () => satirOneki('- ')], ['Numaralı liste', '1. Liste', () => satirOneki('1. ')],
    ['Alıntı', 'Alıntı', () => satirOneki('> ')], ['Bağlantı', 'Bağlantı', baglantiEkle], ['Kütüphaneden görsel ekle', 'Görsel', () => setGorselSec(true)],
  ];

  return (
    <div className="ccd-stack">
      <KaydetCubugu acik={kirli} mesgul={mesgul} kaydet={() => void kaydet()} vazgec={() => { setHata(null); vazgec(); }} />
      <SayfaBaslik baslik={yeni ? 'Yazı ekle' : f.title || 'Yazı'} geri={{ to: '/site/blog', ad: 'Blog yazıları' }}
                   alt={<span className={`ccd-badge${f.status === 'YAYINDA' ? ' ccd-badge-success' : ''}`}>{f.status === 'YAYINDA' ? 'Yayında' : 'Taslak'}</span>}>
        {!yeni && <button type="button" className="ccd-btn ccd-btn-ghost" onClick={() => void sil()}>Sil</button>}
        <button type="button" className="ccd-btn ccd-btn-primary" disabled={mesgul || !kirli} onClick={() => void kaydet()}>{mesgul ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </SayfaBaslik>
      <Sonuc bilgi={bilgi} hata={hata} />

      <div className="ccd-site-iki">
        <div className="ccd-stack ccd-site-iki-ana">
          <Kart>
            <Alan id="sy-baslik" etiket="Başlık">
              <input id="sy-baslik" className="ccd-input" maxLength={160} placeholder="Örn. Sır nasıl hazırlanır?" value={f.title} onChange={(e) => al('title', e.target.value)} />
            </Alan>
            <div className="ccd-field">
              <div className="ccd-site-etiket">
                <label className="ccd-field-label" htmlFor="sy-govde">İçerik</label>
                <Secmeli etiket="İçerik görünümü" secenekler={[['yaz', 'Yaz'], ['onizle', 'Önizle']] as const} deger={icerikSekme} degis={setIcerikSekme} />
              </div>
              {icerikSekme === 'yaz' ? (
                <div className="ccd-site-yazar">
                  <div className="ccd-site-araclar" role="toolbar" aria-label="Biçimlendirme">
                    {ARAC.map(([baslik, ad, is, ic]) => <button key={baslik} type="button" className="ccd-site-arac-dugme" title={baslik} onClick={is}>{ic ?? ad}</button>)}
                  </div>
                  <textarea id="sy-govde" ref={govde} className="ccd-textarea ccd-site-govde" spellCheck value={f.bodyMd}
                            placeholder={'Yazıyı buraya yazın.\n\n## Ara başlık\n\nParagraflar arasında bir satır boş bırakın.'}
                            onChange={(e) => al('bodyMd', e.target.value)} />
                </div>
              ) : (
                <div className="ccd-site-onizleme" aria-label="Önizleme">
                  {govdeMetni.trim() ? <div className="ccd-site-yazi" dangerouslySetInnerHTML={{ __html: onizleme }} /> : <div className="ccd-meta">Yazı boş.</div>}
                </div>
              )}
              <div className="ccd-field-hint">Biçim Markdown'dır; HTML etiketi kabul edilmez.</div>
            </div>
            <Alan id="sy-ozet" etiket="Özet" sayac={`${f.excerpt.length} / 300`} ipucu="Blog listesinde başlığın altında görünür.">
              <textarea id="sy-ozet" className="ccd-textarea" rows={3} maxLength={300} value={f.excerpt} onChange={(e) => al('excerpt', e.target.value.replace(/\n/g, ' '))} />
            </Alan>
            {gorselSec && <GorselSecici sec={gorselEkle} kapat={() => setGorselSec(false)} />}
          </Kart>
        </div>

        <div className="ccd-stack ccd-site-iki-yan">
          <Kart baslik="Görünürlük">
            <div className="ccd-site-radyolar" role="radiogroup" aria-label="Görünürlük">
              {([['TASLAK', 'Taslak', 'Sitede görünmez'], ['YAYINDA', 'Yayında', 'Bir sonraki yayında siteye çıkar']] as const).map(([d, ad, alt]) => (
                <label key={d} className="ccd-site-radyo">
                  <input type="radio" name="sy-durum" checked={f.status === d} onChange={() => al('status', d)} />
                  <span><span className="ccd-site-radyo-ad">{ad}</span><span className="ccd-meta">{alt}</span></span>
                </label>
              ))}
            </div>
            <Alan id="sy-tarih" etiket="Yayın tarihi" ipucu={f.publishedAt ? undefined : 'Boşsa yayına alındığı gün yazılır.'}>
              <input id="sy-tarih" className="ccd-input" type="date" value={f.publishedAt ?? ''} onChange={(e) => al('publishedAt', e.target.value || null)} />
            </Alan>
          </Kart>

          <Kart baslik="Kapak görseli">
            <GorselAlan etiket="" genis deger={f.cover} degis={(v) => al('cover', v)} ipucu="Yatay (16:9) görsel. Yayına almak için gerekli." />
          </Kart>

          <Kart baslik="Dil">
            <Secmeli etiket="Yazının dili" tam secenekler={[['tr', 'Türkçe'], ['en', 'İngilizce']] as const} deger={f.lang} degis={(v) => al('lang', v)} />
          </Kart>

          <Kart baslik="Adres">
            <Alan id="sy-slug" etiket="Adres (slug)" ipucu={yeni ? 'Başlıktan üretilir; isterseniz değiştirin.' : 'Yayındaki yazının adresini değiştirmek eski bağlantıyı kırar.'}>
              <input id="sy-slug" className="ccd-input ccd-mono" maxLength={80} value={adres} placeholder="baslik-buraya"
                     onChange={(e) => { setSlugElle(true); al('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-')); }} />
            </Alan>
          </Kart>

          <Kart baslik="Arama motoru listelemesi"
                eylem={<button type="button" className="ccd-site-metin-dugme" aria-expanded={seoAcik} onClick={() => setSeoAcik(!seoAcik)}>{seoAcik ? 'Kapat' : 'Düzenle'}</button>}>
            <AramaOnizleme baslik={f.seoTitle || f.title} adres={tamAdres} aciklama={f.seoDesc || f.excerpt} />
            {seoAcik && (
              <>
                <Alan id="sy-seob" etiket="SEO başlığı" sayac={`${(f.seoTitle ?? '').length} / 70`} ipucu="Boşsa yazının başlığı kullanılır.">
                  <input id="sy-seob" className="ccd-input" maxLength={70} value={f.seoTitle ?? ''} onChange={(e) => al('seoTitle', e.target.value || null)} />
                </Alan>
                <Alan id="sy-seoa" etiket="SEO açıklaması" sayac={`${(f.seoDesc ?? '').length} / 200`} ipucu="Boşsa özet kullanılır.">
                  <textarea id="sy-seoa" className="ccd-textarea" rows={3} maxLength={200} value={f.seoDesc ?? ''} onChange={(e) => al('seoDesc', e.target.value.replace(/\n/g, ' ') || null)} />
                </Alan>
              </>
            )}
          </Kart>
        </div>
      </div>
    </div>
  );
}
